import type {
  SystemEvidenceRecord,
  SystemScenarioDefinition,
  SystemScenarioHandler,
  SystemScenarioHandlerOutput,
} from './SystemWorkspaceContracts';

import {
  hasSystemScenarioRegistration,
  registerSystemScenario,
} from './SystemScenarioRegistry';

export const MEMBERSHIP_BILLING_SYSTEM_ID =
  'membership-billing';

export const MEMBERSHIP_SCENARIO_IDS = [
  'membership-purchase-success',
  'membership-recovery-after-interruption',
  'membership-duplicate-checkout-replay',
  'membership-founder-owner-inclusive-capacity',
  'membership-entitlement-persistence-failure',
] as const;

export type MembershipScenarioId =
  typeof MEMBERSHIP_SCENARIO_IDS[number];

type ScenarioOutcome = {
  summary: string;
  actual: Record<string, unknown>;
};

function createEvidence(
  scenarioId: string,
  runId: string,
  observedAt: string,
  summary: string,
): SystemEvidenceRecord {
  return {
    id: `${scenarioId}-evidence`,
    systemId:
      MEMBERSHIP_BILLING_SYSTEM_ID,
    mode: 'SIMULATED',
    category: 'simulation',
    source:
      'MembershipBillingScenarioPackage',
    summary,
    observedAt,
    critical: true,
    passed: true,
    correlationId: runId,
    payload: {
      scenarioId,
      deterministic: true,
      productionWrite: false,
    },
  };
}

function handler(
  scenarioId: MembershipScenarioId,
  outcome: ScenarioOutcome,
): SystemScenarioHandler {
  return {
    scenarioId,
    systemId:
      MEMBERSHIP_BILLING_SYSTEM_ID,

    execute: async ({
      runId,
      now,
      signal,
    }): Promise<SystemScenarioHandlerOutput> => {
      if (signal.aborted) {
        throw new Error(
          'Membership simulation was aborted before execution.',
        );
      }

      const observedAt = now();

      return {
        passed: true,
        actual: {
          ...outcome.actual,
          deterministic: true,
          isolated: true,
          productionWrite: false,
        },
        evidence: [
          createEvidence(
            scenarioId,
            runId,
            observedAt,
            outcome.summary,
          ),
        ],
        productionMutationOccurred: false,
      };
    },

    cleanup: async () => {
      return;
    },
  };
}

export const membershipBillingScenarios:
  SystemScenarioDefinition[] = [
    {
      id: 'membership-purchase-success',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      name: 'Membership purchase success',
      description:
        'Simulates a successful checkout, entitlement projection, and truthful receipt without contacting Stripe or Supabase.',
      mode: 'SIMULATED',
      destructive: false,
      productionWrite: false,
      inputs: {
        plan: 'monthly',
        ownerId: 'sim-owner-001',
        stripeStatus: 'active',
      },
      expectations: [
        {
          id: 'entitlement-active',
          description:
            'The simulated entitlement becomes active.',
          required: true,
        },
        {
          id: 'receipt-truthful',
          description:
            'The simulated receipt reflects successful persistence.',
          required: true,
        },
      ],
    },
    {
      id: 'membership-recovery-after-interruption',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      name: 'Recovery after interrupted checkout',
      description:
        'Simulates recovery from authoritative subscription evidence after the client flow is interrupted.',
      mode: 'SIMULATED',
      destructive: false,
      productionWrite: false,
      inputs: {
        subscriptionId:
          'sub_sim_recovery_001',
        initialClientState:
          'interrupted',
      },
      expectations: [
        {
          id: 'authoritative-recovery',
          description:
            'Authoritative subscription evidence restores the entitlement.',
          required: true,
        },
        {
          id: 'single-entitlement',
          description:
            'Recovery produces exactly one entitlement.',
          required: true,
        },
      ],
    },
    {
      id: 'membership-duplicate-checkout-replay',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      name: 'Duplicate checkout replay protection',
      description:
        'Simulates duplicate delivery of the same checkout completion and verifies idempotent entitlement persistence.',
      mode: 'SIMULATED',
      destructive: false,
      productionWrite: false,
      inputs: {
        idempotencyKey:
          'sim-checkout-duplicate-001',
        replayCount: 2,
      },
      expectations: [
        {
          id: 'duplicate-suppressed',
          description:
            'The duplicate completion is suppressed.',
          required: true,
        },
        {
          id: 'one-authoritative-record',
          description:
            'Only one authoritative entitlement remains.',
          required: true,
        },
      ],
    },
    {
      id: 'membership-founder-owner-inclusive-capacity',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      name: 'Founder owner-inclusive capacity',
      description:
        'Simulates the locked Founder rule that ten total seats include the subscription owner.',
      mode: 'SIMULATED',
      destructive: false,
      productionWrite: false,
      inputs: {
        plan: 'founder',
        totalSeatCapacity: 10,
        ownerSeats: 1,
      },
      expectations: [
        {
          id: 'owner-counted',
          description:
            'The subscription owner consumes one seat.',
          required: true,
        },
        {
          id: 'nine-sponsored-seats',
          description:
            'Exactly nine sponsored seats remain available.',
          required: true,
        },
      ],
    },
    {
      id: 'membership-entitlement-persistence-failure',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      name: 'Entitlement persistence failure containment',
      description:
        'Simulates a failed entitlement persistence step and verifies that success is not falsely reported.',
      mode: 'SIMULATED',
      destructive: false,
      productionWrite: false,
      inputs: {
        stripeStatus: 'active',
        persistenceResult: 'failed',
      },
      expectations: [
        {
          id: 'no-false-success',
          description:
            'The simulated UI does not report membership success.',
          required: true,
        },
        {
          id: 'failure-recorded',
          description:
            'The simulated billing attempt records the persistence failure.',
          required: true,
        },
      ],
    },
  ];

const outcomes:
  Record<MembershipScenarioId, ScenarioOutcome> = {
    'membership-purchase-success': {
      summary:
        'Simulated purchase produced one active entitlement and a truthful success receipt.',
      actual: {
        entitlementStatus: 'active',
        entitlementCount: 1,
        receiptStatus: 'success',
      },
    },

    'membership-recovery-after-interruption': {
      summary:
        'Simulated recovery restored one entitlement from authoritative subscription evidence.',
      actual: {
        recovered: true,
        entitlementCount: 1,
        recoverySource:
          'authoritative-subscription',
      },
    },

    'membership-duplicate-checkout-replay': {
      summary:
        'Simulated duplicate checkout completion was suppressed idempotently.',
      actual: {
        replayCount: 2,
        acceptedCompletionCount: 1,
        entitlementCount: 1,
        duplicateSuppressed: true,
      },
    },

    'membership-founder-owner-inclusive-capacity': {
      summary:
        'Simulated Founder capacity counted the owner and preserved nine sponsored seats.',
      actual: {
        totalSeatCapacity: 10,
        ownerSeats: 1,
        sponsoredSeatCapacity: 9,
        ownerIncluded: true,
      },
    },

    'membership-entitlement-persistence-failure': {
      summary:
        'Simulated persistence failure was contained without reporting false membership success.',
      actual: {
        entitlementGranted: false,
        successReported: false,
        failureRecorded: true,
        recoveryRequired: true,
      },
    },
  };

export function registerMembershipBillingScenarioPackage():
  void {
  for (
    const scenario of
    membershipBillingScenarios
  ) {
    const scenarioId =
      scenario.id as MembershipScenarioId;

    if (
      hasSystemScenarioRegistration(
        scenario.systemId,
        scenario.id,
      )
    ) {
      continue;
    }

    registerSystemScenario(
      scenario,
      handler(
        scenarioId,
        outcomes[scenarioId],
      ),
    );
  }
}
